import {useMutation, useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useSnackBar} from "@hooks/alert/useSnackBar";
import {useShowError} from "@hooks/useShowError/useShowError";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {
	createVoyageFromTemplateSingleVoyage,
	createVoyageTemplateFromVoyageSingleVoyage,
	deleteVoyageTemplateSingleVoyage,
	getVoyageTemplateByIdSingleVoyage,
	getSyncPreviewVoyageTemplateSingleVoyage,
	getVoyageTemplatesSingleVoyage,
	saveVoyageTemplateSingleVoyage,
	syncVoyagesVoyageTemplateSingleVoyage
} from "@containers/singleVoyage/voyageTemplates/api/api";
import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {refetchSingleVoyages} from "@containers/singleVoyage/refetchSingleVoyages/refetchSingleVoyages";
import {
	QUERY_KEY_VOYAGE_TEMPLATE_BY_ID_SINGLE_VOYAGES,
	QUERY_KEY_VOYAGE_TEMPLATE_SYNC_PREVIEW_SINGLE_VOYAGES,
	QUERY_KEY_VOYAGE_TEMPLATES_SINGLE_VOYAGES
} from "@containers/singleVoyage/voyageTemplates/queryHooks/constants/queryKey";
import {
	refetchVoyageTemplatesSingleVoyage
} from "@containers/singleVoyage/voyageTemplates/refetch/refetchVoyageTemplatesSingleVoyage";
import {
	IPropsGetVoyageTemplateByIdSingleVoyage,
	IPropsGetVoyageTemplatesSingleVoyage,
	IPropsSyncPreviewVoyageTemplate,
	ISyncItemVoyageTemplate,
	IVoyageTemplateSingleVoyage
} from "@containers/singleVoyage/voyageTemplates/types";
import {getCloseStopsErrorMessage} from "@containers/singleVoyage/containers/closeStops/closeStopsMessages";

export const useGetVoyageTemplatesSingleVoyageQuery = ({
														   params,
														   enabled,
													   }: {
	params: IPropsGetVoyageTemplatesSingleVoyage
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()

	return useQuery({
		queryFn: () => getVoyageTemplatesSingleVoyage(params, onShowError),
		queryKey: [
			QUERY_KEY_VOYAGE_TEMPLATES_SINGLE_VOYAGES,
			params?.accountId?.toString() || '',
			params?.waypointTemplateId?.toString() || ''
		],
		staleTime: 1000 * 60 * 30,
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!params?.accountId,
	})
}

export const useGetVoyageTemplateByIdSingleVoyageQuery = ({
															  params,
															  enabled,
														  }: {
	params: IPropsGetVoyageTemplateByIdSingleVoyage
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()

	return useQuery({
		queryFn: () => getVoyageTemplateByIdSingleVoyage(params, onShowError),
		queryKey: [
			QUERY_KEY_VOYAGE_TEMPLATE_BY_ID_SINGLE_VOYAGES,
			params?.accountId?.toString() || '',
			params?.id?.toString() || ''
		],
		staleTime: 1000 * 60 * 30,
		refetchOnMount: 'always',
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!params?.accountId && !!params?.id,
	})
}

export const useSaveVoyageTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: (template: IVoyageTemplateSingleVoyage) => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: saveVoyageTemplateSingleVoyage,
		onSuccess: (template) => {
			refetchVoyageTemplatesSingleVoyage()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess(template)
		},
		onError: (e) => {
			setSnackBar({
				message: getCloseStopsErrorMessage(e) ?? buildErrorsMessages(e),
				variant: 'error'
			})
		}
	})
}

export const useCreateVoyageFromTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: (voyage: ISingleVoyage) => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: createVoyageFromTemplateSingleVoyage,
		onSuccess: (voyage) => {
			// the planning grid reads planned voyages to fill its slots
			refetchSingleVoyages()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess(voyage)
		},
		onError: (e) => {
			setSnackBar({
				message: getCloseStopsErrorMessage(e) ?? buildErrorsMessages(e),
				variant: 'error'
			})
		}
	})
}

export const useCreateVoyageTemplateFromVoyageSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: (template: IVoyageTemplateSingleVoyage) => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: createVoyageTemplateFromVoyageSingleVoyage,
		onSuccess: (template) => {
			refetchVoyageTemplatesSingleVoyage()
			setSnackBar({
				title: t('singleVoyage.voyageTemplate.createdFromVoyage'),
				variant: 'info'
			})
			onSuccess && onSuccess(template)
		},
		onError: (e) => {
			setSnackBar({
				message: getCloseStopsErrorMessage(e) ?? buildErrorsMessages(e),
				variant: 'error'
			})
		}
	})
}

export const useDeleteVoyageTemplateSingleVoyageQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: deleteVoyageTemplateSingleVoyage,
		onSuccess: () => {
			refetchVoyageTemplatesSingleVoyage()
			setSnackBar({
				title: t('successDeleteMessage'),
				variant: 'info'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	})
}

export const useGetSyncPreviewVoyageTemplateQuery = ({
														 params,
														 enabled,
													 }: {
	params: IPropsSyncPreviewVoyageTemplate
	enabled?: boolean
}) => {
	const {onShowError} = useShowError()

	return useQuery({
		queryFn: () => getSyncPreviewVoyageTemplateSingleVoyage(params, onShowError),
		queryKey: [
			QUERY_KEY_VOYAGE_TEMPLATE_SYNC_PREVIEW_SINGLE_VOYAGES,
			params?.accountId?.toString() || '',
			params?.templateId?.toString() || ''
		],
		// voyages change under the preview, so it is never served from cache - but it is not
		// refetched on focus either, that would reset the user's selection
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: false,
		enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!params?.accountId && !!params?.templateId,
	})
}

export const useSyncVoyagesVoyageTemplateQuery = ({onSuccess}: {
	onSuccess?: (results: ISyncItemVoyageTemplate[]) => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: syncVoyagesVoyageTemplateSingleVoyage,
		onSuccess: (results) => {
			refetchSingleVoyages()
			refetchVoyageTemplatesSingleVoyage()
			const updated = results.filter((it) => it.outcome === 'UPDATED').length
			setSnackBar({
				title: t('singleVoyage.voyageTemplate.sync.result', {
					updated,
					skipped: results.length - updated,
				}),
				variant: updated === results.length ? 'info' : 'warning'
			})
			onSuccess && onSuccess(results)
		},
		onError: (e) => {
			setSnackBar({
				message: buildErrorsMessages(e),
				variant: 'error'
			})
		}
	})
}
